import { Card, CardContent, Typography, useTheme } from '@mui/joy';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DATE_MONTH_YEAR_SHORT_FORMAT } from 'core/constants';
import { formatDate } from 'core/utils';
import { ITssDashboardFilters, LineChart } from 'reports/components';
import { CHART_COLORS } from 'reports/constants';
import { useGetBomHeader } from 'reports/hooks';
import { IBomHeaderJob } from 'reports/types';
import { Level, ModeType, Variant } from 'theme/enums';

const MONTHS_TO_DISPLAY = 12;

interface IMonth {
  label?: string;
  month: number;
  year: number;
}

interface IProps {
  title: string;
  startDate: string;
  endDate: string;
  filters?: ITssDashboardFilters;
  height: number | string;
}

export const TrendingJobsChart = ({ title, startDate, endDate, filters, height }: IProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === ModeType.DARK;
  const paletteColors = isDarkMode ? CHART_COLORS.palette.dark : CHART_COLORS.palette.light;

  const requestPayload = useMemo(
    () => ({
      filters: {
        ...filters,
        jobCreatedAtSince: startDate,
        jobCreatedAtUntil: endDate,
      },
    }),
    [startDate, endDate, filters]
  );

  const { records, isLoading, isFetching } = useGetBomHeader(requestPayload);

  const isDataLoading = isLoading || isFetching;

  const transformedData = useMemo(() => {
    if (!records.length) {
      return { datasets: [], labels: [] };
    }

    const months = getMonths();

    const theatreData: Record<string, number[]> = {};

    records.forEach((job: IBomHeaderJob) => {
      const theatreName = job.theatreName || t('labelUnknown');
      const jobDate = new Date(job.jobCreatedAt);
      const jobMonth = jobDate.getMonth();
      const jobYear = jobDate.getFullYear();

      if (!theatreData[theatreName]) {
        theatreData[theatreName] = new Array(MONTHS_TO_DISPLAY).fill(0);
      }

      const monthIndex = months.findIndex(
        (month) => month.month === jobMonth && month.year === jobYear
      );

      if (monthIndex !== -1) {
        theatreData[theatreName][monthIndex]++;
      }
    });

    const datasets = Object.entries(theatreData)
      .map(([theatreName, data]) => ({
        data,
        label: theatreName,
        totalBoms: data.reduce((sum, val) => sum + val, 0),
      }))
      .sort((a, b) => b.totalBoms - a.totalBoms)
      .map((item, index) => ({
        borderColor: paletteColors[index % paletteColors.length],
        data: item.data,
        label: item.label,
      }));

    return {
      datasets,
      labels: months.map((month) => month.label),
    };
  }, [paletteColors, records, t]);

  return (
    <Card variant={Variant.OUTLINED}>
      <CardContent>
        <Typography level={Level.TITLE_MD} sx={{ marginBottom: 2 }}>
          {title}
        </Typography>
        <LineChart data={transformedData} isLoading={isDataLoading} height={height} />
      </CardContent>
    </Card>
  );
};

function getMonths(): IMonth[] {
  const months: IMonth[] = [];
  const now = new Date();

  for (let i = MONTHS_TO_DISPLAY - 1; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      label: formatDate(date, false, DATE_MONTH_YEAR_SHORT_FORMAT),
      month: date.getMonth(),
      year: date.getFullYear(),
    });
  }

  return months;
}
